import React from 'react'
import styled from 'styled-components'
import { Icon } from '../../atoms/Icon'
import { IconButton as LibIconButton } from '../../atoms/IconButton'

// Container components
export const FilterPanelContainer = styled.div<{ $collapsed?: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? '0' : '340px')};
  background: ${({ theme }) => theme.colors.background.primary};
  border-right: 1px solid ${({ theme }) => theme.colors.border.default};
  position: relative;
  transition: width ${({ theme }) => theme.transitions.default};
  overflow-x: hidden;
  overflow-y: visible;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    position: fixed;
    right: 0;
    top: 0;
    height: 100vh;
    width: ${({ $collapsed }) => ($collapsed ? '0' : '100%')};
    z-index: 1000;
  }

  @media print {
    display: none !important;
    visibility: hidden !important;
    width: 0 !important;
  }
`

// Header components
export const FilterHeader = styled.div`
  padding-top: ${({ theme }) => theme.spacing.medium};
  padding-bottom: ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
`

export const FilterHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
`

export const FilterTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin: 0;
`

export const FilterCount = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
  display: inline-block;
`

export const FilterActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

// Using IconButton from library instead of custom implementation
export const IconButton = LibIconButton

// Quick filters components
export const QuickFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  margin-top: ${({ theme }) => theme.spacing.medium};
`

export const QuickFiltersTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const QuickFiltersActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
  min-height: 24px;
`

export const QuickFilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  white-space: nowrap;

  & > button {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.primary};
    transition: color ${({ theme }) => theme.transitions.default};

    &:hover {
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }
`

// Content area
export const FilterContent = styled.div`
  flex: 1;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.text.secondary};
  }
`

// Toggle button
export const FilterToggleButton = styled.button<{ $collapsed?: boolean }>`
  position: absolute;
  right: -40px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 80px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-left: none;
  border-radius: ${({ theme }) =>
    `0 ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0`};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  @media (max-width: 768px) {
    display: none;
  }
`

// Filter Panel Component
interface FilterPanelProps {
  title?: string
  filterCount?: number
  collapsed?: boolean
  onToggle?: () => void
  onClear?: () => void
  onSave?: () => void
  quickFilters?: Array<{
    label: string
    onRemove: () => void
  }>
  children: React.ReactNode
  className?: string
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  title = 'Filters',
  filterCount = 0,
  collapsed = false,
  onToggle,
  onClear,
  onSave,
  quickFilters = [],
  children,
  className,
}) => {
  return (
    <FilterPanelContainer $collapsed={collapsed} className={className}>
      <FilterHeader>
        <FilterHeaderTop>
          <FilterTitle>
            {title}
            {filterCount > 0 && <FilterCount>{filterCount}</FilterCount>}
          </FilterTitle>
          <FilterActions>
            {onSave && (
              <IconButton onClick={onSave} aria-label="Save filters">
                <Icon name="arrow-down" size="small" />
              </IconButton>
            )}
            {onClear && filterCount > 0 && (
              <IconButton onClick={onClear} aria-label="Clear filters">
                <Icon name="refresh" size="small" />
              </IconButton>
            )}
            {onToggle && (
              <IconButton
                onClick={onToggle}
                aria-label="Close filters"
                variant="danger"
              >
                <Icon name="x" size="small" />
              </IconButton>
            )}
          </FilterActions>
        </FilterHeaderTop>

        {quickFilters.length > 0 && (
          <QuickFilters>
            <QuickFiltersTitle>Quick Filters</QuickFiltersTitle>
            <QuickFiltersActions>
              {quickFilters.map((filter, index) => (
                <QuickFilterChip key={index}>
                  {filter.label}
                  <button onClick={filter.onRemove}>
                    <Icon name="x" size="small" />
                  </button>
                </QuickFilterChip>
              ))}
            </QuickFiltersActions>
          </QuickFilters>
        )}
      </FilterHeader>

      <FilterContent>{children}</FilterContent>

      {onToggle && (
        <FilterToggleButton $collapsed={collapsed} onClick={onToggle}>
          <Icon name={collapsed ? 'arrow-right' : 'arrow-left'} size="small" />
        </FilterToggleButton>
      )}
    </FilterPanelContainer>
  )
}
